/**
 * TypeScript view of the `rush_hour` format.
 * Source of truth: `shared/schema/rush_hour.{definition,submission,solution,progress}.json`.
 */

export type Localized = Record<string, string | undefined>;

export interface RushLimits {
  maxMoves?: number | null;
}

export interface RushDefinition {
  puzzleType: "rush_hour";
  typeVersion: number;
  /**
   * Six rows of six characters: '.' or 'o' empty, 'x' a wall, an upper-case letter a vehicle
   * (two or three cells in a straight line), 'A' the red car, which leaves by the right edge
   * of its own row.
   */
  board: string[];
  limits?: RushLimits;
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface RushProgress {
  version: 1;
  /** The slides so far, e.g. "B+2". Replaying them from the definition gives the lot. */
  moves: string[];
  /** The tail taken back, still redoable. Never sent to the server. */
  undone?: string[];
}

export interface RushSubmission {
  moves: string[];
}

/** The author's reference sequence of slides, in the submission format. */
export type RushSolution = RushSubmission;

export const SIZE = 6;
export const RED = "A";
export const WALL = "x";
export const EMPTY = ".";
export const MAX_MOVES = 1000;
export const MOVE_PATTERN = /^([A-Z])([+-])([1-5])$/;

/** An arrow on the pad or the keyboard. Only the two along a vehicle's lane can move it. */
export type Direction = "u" | "d" | "l" | "r";

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
